import { Suspense, useMemo, useState, useRef, useEffect } from "react"

// ** Third Party Components
import axios from "axios"
import { Delta } from "quill"
import { he } from "date-fns/locale"
import { parseCookies } from "nookies"
import { MessageSquare } from "lucide-react"
import Skeleton from "react-loading-skeleton"
import { format, formatDistance } from "date-fns"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

// ** Theme Components
import { Button } from "../common/button"
import Loader from "@components/common/loader"
import { ScrollArea } from "../common/scroll-area"
import { Tooltip, TooltipContent, TooltipTrigger } from "../common/tooltip"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../common/sheet"

// ** Api Routes
import { SERVER_URL, WORK_DIARY_GET_NOTE_BY_ID } from "@pma/utils/api-routes"

// ** Type & Constants
import { DictType } from "@pma/types"
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Helpers
import { getUserFromId, parseMessage, cn, formatMentions } from "@pma/utils/helpers"

// ** Store
import { useAuth } from "@pma/stores/auth-store"
import { useDrawer } from "@pma/stores/drawer-store"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import RichEditorQuill from "../common/rich-editor-quill"

// Skeleton component for comment items
const CommentSkeleton = () => {
  const { locale } = useTranslator()
  const isHebrew = locale === "he"
  return (
    <div
      className={cn(
        "border-b border-gray-100 py-3 last:border-b-0 dark:border-gray-800",
        isHebrew ? "rtl" : "ltr",
      )}
    >
      <div className='flex items-start gap-3'>
        {/* Avatar Skeleton */}
        <Skeleton circle width={32} height={32} />

        {/* Content Skeleton */}
        <div className='min-w-0 flex-1'>
          {/* Name and Date */}
          <div className='flex items-center gap-2'>
            <Skeleton width={80} height={16} />
            <Skeleton width={120} height={12} />
          </div>

          {/* Message Content */}
          <div className='mt-2'>
            <Skeleton count={3} height={12} style={{ marginBottom: 6 }} />
          </div>
        </div>
      </div>
    </div>
  )
}

// Multiple skeletons for loading state
const CommentsListSkeleton = ({ count = 4 }: { count?: number }) => {
  return (
    <div className='space-y-0 px-4 py-3 sm:px-5'>
      {Array.from({ length: count }).map((_, index) => (
        <CommentSkeleton key={index} />
      ))}
    </div>
  )
}

const WorkDiariesComment = () => {
  const drawer = useDrawer()
  const { authConfig } = useAuth()
  const queryClient = useQueryClient()
  const { translate, locale } = useTranslator()
  const editorRef = useRef<HTMLDivElement>(null)

  const isHebrew = locale === "he"
  const workDiaryId = drawer?.workDiaryId
  const sheetSide = isHebrew ? "left" : "right"
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const organizationUsers = authConfig?.organizationUsers
  const mentionUsers = organizationUsers
    ?.filter((user: DictType) => user?.name)
    .map((user: DictType) => {
      return {
        id: user?.id,
        value: user?.name,
        url: user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`,
      }
    })

  const isOpen = drawer.context === "word-diary-comment" && drawer.isOpen
  const isBothApproved =
    drawer.workDiaryApprovals?.contractorApproval && drawer.workDiaryApprovals?.supervisorApproval

  const [message, setMessage] = useState("")
  const [isEditorExpanded, setIsEditorExpanded] = useState(false)

  const closeDrawer = () => {
    queryClient.removeQueries({
      queryKey: [`work-diary-comments-${workDiaryId}`],
    })
    setMessage("")
    drawer.setWorkDiaryId("")
    drawer.onClose()
    setIsEditorExpanded(false)
  }

  // Close editor when clicking outside (only if message is empty)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        editorRef.current &&
        !editorRef.current.contains(event.target as Node) &&
        message === ""
      ) {
        setIsEditorExpanded(false)
      }
    }

    if (isEditorExpanded) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isEditorExpanded, message])

  const fetchComments = async () => {
    if (!workDiaryId) return

    const response = await axios.get(SERVER_URL + WORK_DIARY_GET_NOTE_BY_ID + `/${workDiaryId}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    })
    return response.data
  }

  const { data, isLoading, refetch } = useQuery({
    queryKey: [`work-diary-comments-${workDiaryId}`, workDiaryId],
    queryFn: fetchComments,
    enabled: !!workDiaryId,
  })

  const commentList = useMemo(
    () => data?.sort((a: DictType, b: DictType) => b.created_at - a.created_at) || [],
    [data],
  )

  const handleChange = (e: Delta) => {
    const data = JSON.stringify(e.ops)
    setMessage(data)
  }

  const createCommentMessageApi = async (note: string) => {
    const response = await axios.post(
      SERVER_URL + "/notes/add",
      {
        note: JSON.stringify(note),
        context_id: workDiaryId,
        context_type: "diary",
      },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )
    return response.data
  }

  const addNewCommentInWorkDiaryMutation = useMutation({
    mutationKey: ["add-new-work-diary-comment"],
    mutationFn: (note: string) => createCommentMessageApi(message),
    onSuccess: async () => {
      setMessage("")
      setIsEditorExpanded(true) // Keep editor open after submit
      toast({
        title: translate("Success"),
        description: translate("Comment insert successfully"),
        variant: "success",
      })
      refetch()
      await queryClient.invalidateQueries({ queryKey: ["work-diaries-context"] })
      await queryClient.invalidateQueries({ queryKey: ["get-work-diary-details"] })
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error occurred"),
        variant: "error",
      })
    },
  })

  const onSubmit = () => {
    if (message === "") return
    return addNewCommentInWorkDiaryMutation.mutate(formatMentions(message))
  }

  // Get user initials for avatar
  const getUserInitials = (name: string) => {
    if (!name) return "?"
    const parts = name.split(" ")
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  // Get avatar background color based on name
  const getAvatarColor = (name: string) => {
    const colors = [
      "bg-blue-500",
      "bg-green-500",
      "bg-purple-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-teal-500",
      "bg-orange-500",
      "bg-cyan-500",
    ]
    if (!name) return colors[0]
    const index = name.charCodeAt(0) % colors.length
    return colors[index]
  }

  return (
    <Suspense fallback={<Loader />}>
      <Sheet open={isOpen} onOpenChange={closeDrawer}>
        <SheetContent
          className='flex h-full w-full flex-col bg-white p-0 dark:bg-gray-900 sm:w-[420px] md:w-[480px] lg:w-[520px]'
          side={sheetSide}
        >
          {/* Header */}
          <SheetHeader className='shrink-0 border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800 sm:px-5'>
            <div className={cn("flex items-center justify-between", isHebrew ? "rtl" : "ltr")}>
              <div className='flex items-center gap-2'>
                <MessageSquare className='h-5 w-5 text-gray-500 dark:text-gray-400' />
                <SheetTitle className='text-base font-semibold text-gray-800 dark:text-gray-200'>
                  {translate("Comments")}
                </SheetTitle>
              </div>
            </div>
          </SheetHeader>

          <div className='flex flex-1 flex-col overflow-hidden'>
            {/* Editor Section - Expandable (Open by default) */}
            {!isBothApproved && (
              <div className='shrink-0 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800 sm:px-5'>
                <div ref={editorRef}>
                  {/* Collapsed State - Simple Input */}
                  <div
                    onClick={() => setIsEditorExpanded(true)}
                    className={cn(
                      "cursor-text rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-500 transition-all hover:border-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:border-gray-500",
                      isEditorExpanded && "hidden",
                      isHebrew ? "rtl" : "ltr",
                    )}
                  >
                    {translate("Write a comment")}...
                  </div>

                  {/* Expanded State - Full Editor */}
                  <div
                    className={cn(
                      "flex flex-col overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm transition-all duration-300 ease-in-out dark:border-gray-600 dark:bg-gray-700",
                      isEditorExpanded ? "max-h-[300px] opacity-100" : "max-h-0 border-0 opacity-0",
                    )}
                  >
                    {/* Rich Text Editor - Scrollable */}
                    <div className='max-h-[220px] min-h-[80px] overflow-y-auto px-3 py-2 text-sm text-gray-700 dark:text-gray-200'>
                      <RichEditorQuill
                        placeHolder={`${translate("Write a comment")}...`}
                        handleChange={handleChange}
                        mentionUsers={mentionUsers}
                        id={workDiaryId?.toString()}
                        value={message}
                      />
                    </div>

                    {/* Footer Actions - Always visible */}
                    <div
                      className={cn(
                        "flex shrink-0 items-center border-t border-gray-200 bg-white px-3 py-2.5 dark:border-gray-600 dark:bg-gray-700",
                        isHebrew ? "rtl" : "ltr",
                      )}
                    >
                      <Button
                        size='sm'
                        onClick={onSubmit}
                        disabled={message === "" || addNewCommentInWorkDiaryMutation.isPending}
                        loading={addNewCommentInWorkDiaryMutation.isPending}
                        className='h-8 rounded bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'
                      >
                        {translate("Save")}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Comments List - Scrollable with Skeleton Loading */}
            <ScrollArea className='flex-1 bg-white dark:bg-gray-900'>
              {isLoading ? (
                <CommentsListSkeleton count={4} />
              ) : commentList.length === 0 ? (
                <div className='flex flex-col items-center justify-center py-12 text-center'>
                  <MessageSquare className='mb-3 h-12 w-12 text-gray-300 dark:text-gray-600' />
                  <p className='text-sm font-medium text-gray-500 dark:text-gray-400'>
                    {translate("No comments yet")}
                  </p>
                  <p className='mt-1 text-xs text-gray-400 dark:text-gray-500'>
                    {translate("Be the first to add a comment")}
                  </p>
                </div>
              ) : (
                <div className='divide-y divide-gray-200 px-4 py-3 dark:divide-gray-700 sm:px-5 '>
                  {commentList.map((comment: any, index: number) => {
                    const userName =
                      getUserFromId(comment?.user_id, organizationUsers)?.name ||
                      comment.user?.name ||
                      "Unknown"
                    const userInitials = getUserInitials(userName)
                    const avatarColor = getAvatarColor(userName)

                    return (
                      <div className='flex items-start gap-3 py-3'>
                        {/* Avatar */}
                        {comment?.user?.user_picture?.url ? (
                          <img
                            alt={userName}
                            src={comment.user.user_picture.url}
                            className='h-8 w-8 shrink-0 rounded-full object-cover'
                          />
                        ) : (
                          <div
                            className={cn(
                              "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white",
                              avatarColor,
                            )}
                          >
                            {userInitials}
                          </div>
                        )}

                        {/* Content */}
                        <div className='min-w-0 flex-1 overflow-hidden'>
                          {/* Name and Date */}
                          <div className='flex flex-wrap items-center gap-x-2 gap-y-0.5'>
                            <span className='text-sm font-semibold text-gray-800 dark:text-gray-200'>
                              {userName}
                            </span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className='text-xs text-blue-600 hover:underline dark:text-blue-400'>
                                  {format(comment?.created_at, "d MMM yyyy, HH:mm", {
                                    locale: isHebrew ? he : undefined,
                                  })}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <span className='text-xs'>
                                  {formatDistance(comment?.created_at, new Date(), {
                                    locale: isHebrew ? he : undefined,
                                    addSuffix: true,
                                  })}
                                </span>
                              </TooltipContent>
                            </Tooltip>
                          </div>

                          {/* Message Content */}
                          <div
                            dangerouslySetInnerHTML={{
                              __html: parseMessage(comment?.note, organizationUsers),
                            }}
                            className={cn(
                              "mt-1.5 text-sm leading-relaxed text-gray-700 dark:text-gray-300",
                              "[overflow-wrap:anywhere] [word-break:break-word]",
                              "[&_a]:rounded [&_a]:bg-blue-100 [&_a]:px-1.5 [&_a]:py-0.5 [&_a]:text-blue-600 [&_a]:no-underline [&_a]:[word-break:break-all] [&_a]:hover:bg-blue-200 dark:[&_a]:bg-blue-900/40 dark:[&_a]:text-blue-400",
                              "[&_.mention]:rounded [&_.mention]:bg-blue-100 [&_.mention]:px-1 [&_.mention]:text-blue-600 dark:[&_.mention]:bg-blue-900/40 dark:[&_.mention]:text-blue-400",
                            )}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
        </SheetContent>
      </Sheet>
    </Suspense>
  )
}

export default WorkDiariesComment
